import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take, timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ModulePermissionsActions } from '../../store/module-permissions/module-permissions.actions';
import { selectPermissionsLoaded } from '../../store/module-permissions/module-permissions.selectors';

/**
 * APP_INITIALIZER factory that loads module permissions before the app starts.
 *
 * This ensures permissions are ready before any route guards execute,
 * preventing race conditions and unnecessary delays during navigation.
 *
 * Timeout: 10 seconds (generous for slow networks)
 * On timeout/error: allows app to start (permissions will be empty, blocking all routes)
 */
export function initializeModulePermissions() {
  const store = inject(Store);

  return (): Promise<void> => {
    // Dispatch the load action
    store.dispatch(ModulePermissionsActions.loadPermissions());

    // Wait for permissions to be loaded (or timeout after 10s)
    return new Promise<void>((resolve) => {
      store.select(selectPermissionsLoaded)
        .pipe(
          filter(loaded => loaded),
          take(1),
          timeout(10000),
          catchError((error) => {
            console.error('[ModulePermissions] Failed to load permissions during app initialization:', error);
            // Resolve anyway to allow app to start (guards will block navigation)
            return of(true);
          })
        )
        .subscribe({
          next: () => {
            console.log('[ModulePermissions] Permissions loaded successfully');
            resolve();
          },
          error: () => {
            // Fallback in case catchError doesn't handle it
            console.error('[ModulePermissions] Unexpected error in permissions loading');
            resolve();
          }
        });
    });
  };
}
